import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { LoaderService } from 'src/app/services/loader.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { defer, Subject } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ForaeldreloestAfregningsgrundlagService } from 'src/app/api/services';
import { ForaeldreloestAfregningsgrundlagDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/foraeldreloest-afregningsgrundlag-dto';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ForaeldreloeseFilter, ServiceordreFilter } from 'src/app/components/aftale/models';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { GetForaldreloeseRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-foraldreloese-request';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { GetForaledreloeseResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-foraledreloese-response';
import { HttpErrorResponse } from '@angular/common/http';
import {PaginationCacheService} from '../../../../services/paginationcache.service';

@Component({
    selector: 'app-uparented-orders-list',
    templateUrl: './uparented-orders-list.component.html',
    styleUrls: ['./uparented-orders-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UparentedOrdersListComponent implements OnInit, OnDestroy {
  rowCount: number;
  pageCount: number;

  filterKey: string = 'unparented-filter';
  filter: ServiceordreFilter = {
    serviceOrderStateIds: ([] = []),
    opgaveIds: ([] = []),
    partnerNos: ([] = []),
    orderBy: 'Ajourtid',
    orderDir: 'desc',
  };

  constructor(
    private unparentedOrdersService: ForaeldreloestAfregningsgrundlagService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorage: LocalStorageService,
    private paginationCache: PaginationCacheService
  ) {}

  activeColumns: string[] = ['Oprettid', 'DeliveryDate', 'Opgave', 'PartnerNo', 'PartnerName', 'Description', 'Notice', 'OkKundeAftalenr'];
  unsubscribe$ = new Subject<void>();
  unparentedOrderAccountings: ForaeldreloestAfregningsgrundlagDto[] = [];
  dataSource: MatTableDataSource<ForaeldreloestAfregningsgrundlagDto>;

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.unparentedOrderAccountings = [];

    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          const filter: ForaeldreloeseFilter = {
            partnerNos: ([] = []),
            orderBy: 'Ajourtid',
            orderDir: 'desc',
          };

          const localFilter: ServiceordreFilter = this.localStorage.get(this.filterKey);
          if (localFilter) {
            filter.orderBy = localFilter.orderBy;
            filter.orderDir = localFilter.orderDir;
            filter.partnerNos = localFilter.partnerNos ?? [];
          }

          this.page = 1;

          const q = queryParams['q'];
          const partnerNos = queryParams['partnerNos'];
          const orderBy = queryParams['orderBy'];
          const orderDir = queryParams['orderDir'];

          if (orderBy && orderBy != '') {
            filter.orderBy = orderBy;
          }

          if (orderDir && orderDir != '') {
            filter.orderDir = orderDir;
          }

          if (partnerNos) {
            if (partnerNos instanceof Array) {
              filter.partnerNos = partnerNos.map(function (x: string) {
                return Number(x);
              });
            } else {
              filter.partnerNos.push(Number(partnerNos));
            }
          }

          filter.partnerNos = filter.partnerNos.filter((v, i, a) => a.indexOf(v) === i);

          this.localStorage.set(this.filterKey, filter);

          this.filter = filter;

          const pagination = this.paginationCache.position;
          if (pagination) {
            this.page = Number(pagination.page);
            this.prevRow = pagination.id;
          }

          return this.getUnparented$.pipe(
            tap((response) => {
              this.skip = this.page - 1;
              this.rowCount = response.rowCount;
              this.pageCount = response.pageCount;
              this.unparentedOrderAccountings = response.foraeldreloese;
              this.dataSource = new MatTableDataSource(this.unparentedOrderAccountings);
            })
          );
        })
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  getUnparented$ = defer(() => {
    this.loader.show();
    let top = this.page - this.skip;
    let skip = this.skip > this.page ? top - 1 : this.skip;
    const request: GetForaldreloeseRequest = {
      pageTop: top < 1 ? 1 : top,
      pageSkip: skip < 0 ? 0 : skip,
      partnerNos: this.filter.partnerNos,
      orderBy: this.filter.orderBy,
      orderDir: this.filter.orderDir,
    };

    return this.unparentedOrdersService.getForaldreloese({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response: GetForaledreloeseResponse) => {
        this.rowCount = response.rowCount;
        this.pageCount = response.pageCount;
        this.unparentedOrderAccountings = this.unparentedOrderAccountings.concat(response.foraeldreloese);
        this.dataSource = new MatTableDataSource(this.unparentedOrderAccountings);
        this.dataSource.sort = this.sort;
      }),
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$)
    );
  });

  onRowClick(row: ForaeldreloestAfregningsgrundlagDto) {
    this.router.navigate(['/ordre/unparented', row.id, {page: this.page}]);
  }

  more() {
    this.page++;
    this.skip++;
    this.loader.show();
    this.getUnparented$
      .pipe(
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(),
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
        this.page -= 1;
      };
  }

  sortData(sort: Sort) {
    this.unparentedOrderAccountings = [];
    const orderParams: Params = {
      orderBy: sort.active ? sort.active : null,
      orderDir: sort.direction ? sort.direction : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: orderParams,
      queryParamsHandling: 'merge',
    });
  }

  skip: number = 0;
  page: number = 1;
  prevRow: number = null;
  globalActiveRowID: string = '';
  highLight(id: number) {
    var row = this.unparentedOrderAccountings.find(p => p.id == id);
    if (row) this.globalActiveRowID = row.id.toString();
  }
}
